// The GSA array record for the RS ID being edited, read from the MedQuery registry (whose on-array fields are the manifest's).
import React, { useEffect, useState } from "react";
import { Button, Form } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import InlineLoading from "../../../../components/shared/InlineLoading";
import { useNotifications } from "../../../../context/NotificationContext";
import { RegistryVariant, RegistryVariantLookup } from "../../../../types/geneCodes/RegistryTypes";
import { isCompleteRs, lookupVariant } from "../../../../utils/geneCodes/variantEvidence";
import "./wheelBuilder.css";

const GSA_ARRAY = "GSA";

// The manifest's InExon, Transcripts and Mutations columns never reached the registry, so the pane cannot show them.
const FIELDS: { label: string; testId: string; read: (doc: RegistryVariant) => string }[] = [
	{ label: "Chromosome", testId: "gsa-chr", read: (doc) => doc.chrom ?? "—" },
	{ label: "Map position", testId: "gsa-map", read: (doc) => doc.pos?.toLocaleString() ?? "—" },
	{ label: "Alleles", testId: "gsa-alleles", read: (doc) => doc.alleles ?? "—" },
	{ label: "Gene", testId: "gsa-gene", read: (doc) => (doc.gene_symbols.length > 0 ? doc.gene_symbols.join(", ") : "—") },
];

const emptyHint = (rs: string, lookup: RegistryVariantLookup): string => {
	if (lookup.status === "unavailable") return `The registry could not be read for ${rs}.`;
	if (lookup.status === "found" && lookup.doc.on_array === null) return `The registry does not say whether ${rs} is on the GSA array.`;
	return `${rs} is not on the GSA array.`;
};

const SnpGsaReference: React.FC = () => {
	const form = Form.useFormInstance();
	const { showInfo } = useNotifications();
	const watched = Form.useWatch("rs", form);
	const rs = typeof watched === "string" ? watched.trim() : "";
	const complete = isCompleteRs(rs);

	const [lookup, setLookup] = useState<RegistryVariantLookup | null>(null);

	useEffect(() => {
		setLookup(null);
		if (!complete) return undefined;

		let live = true;
		void lookupVariant(rs).then((result) => {
			if (live) setLookup(result);
		});

		return () => {
			live = false;
		};
	}, [rs, complete]);

	if (!complete) return null;

	const record = lookup?.status === "found" && lookup.doc.on_array === true && lookup.doc.array_names.includes(GSA_ARRAY) ? lookup.doc : null;

	const fillFromGsa = () => {
		if (!record) return;
		form.setFieldsValue({
			gene: record.gene_symbols[0] || form.getFieldValue("gene"),
			array_type: GSA_ARRAY,
		});
		showInfo("Filled from the GSA record.", "Gene and array type.");
	};

	return (
		<div className="mg-node-editor__gsa" data-testid="node-editor-gsa">
			<div className="mg-node-editor__gsa-head">
				<span className="mg-node-editor__label">GSA data reference</span>
				<Button size="small" icon={<DownloadOutlined />} disabled={!record} onClick={fillFromGsa} data-testid="node-editor-gsa-fill">
					Use GSA data
				</Button>
			</div>

			{!lookup ? (
				<InlineLoading message="Loading GSA data…" minHeight={72} />
			) : record ? (
				<div className="mg-node-editor__gsa-grid">
					{FIELDS.map((field) => (
						<div key={field.testId} className="mg-node-editor__gsa-cell">
							<span className="mg-node-editor__hint">{field.label}</span>
							<span className="mg-node-editor__gsa-value" data-testid={field.testId}>
								{field.read(record)}
							</span>
						</div>
					))}
				</div>
			) : (
				<span className="mg-node-editor__hint" data-testid="node-editor-gsa-empty">
					{emptyHint(rs, lookup)}
				</span>
			)}
		</div>
	);
};

export default SnpGsaReference;
